import { z } from 'zod';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';

/**
 * Контракты Блока 8 «Партнёры» (клиентский каталог подрядчиков). Спека поверхности —
 * ADR-0080. PRD `docs/prd/v4/12-block-8-partners.md`, dev-ТЗ `TRD_Партнёры_Блок8`.
 *
 * Зеркалит `furniture.ts`, но БЕЗ kind-дискриминатора, бренда и Lab: подрядчик —
 * единая сущность (P-033-разделение не нужно). Подкатегории «Распил…» — СПИСОК
 * `subcategories: string[]` (ADR-0097: подрядчик может относиться к нескольким). Все
 * текстовые поля — **verbatim** (`z.string()`), без regex/transform/normalize (P-001):
 * связь с будущими именами фото партнёров в R2 ломается при любой нормализации
 * (урок апреля-2026).
 *
 * Фильтр — по ДВУМ триггерам «Услуга» (`category`) + «Город» (`city`), точное
 * совпадение со справочником (не free-text). Карточка показывает только заполненные
 * поля — описательные (`description`/`workingHours`/`photoUrl`) приходят `null`, пока
 * Aibek их не внёс (разрежённая, как декор/фурнитура).
 *
 * Города НЕ дублируем: `CitiesResponseSchema`/`CitiesResponse` импортируются из
 * `./handles` (общий контракт декор/ручек/красок/фурнитуры) — как и магазины.
 */

/** Полная карточка партнёра (Экран 2.8.3) — только заполненные поля, остальные null. */
export const PartnerSchema = z.object({
  id: z.string().min(1),
  /** = «Услуга», каноничное значение из справочника (точное совпадение в фильтре). */
  category: z.string().min(1),
  /**
   * Подкатегории подрядчика (ADR-0097): подрядчик «Распила» может относиться к
   * НЕСКОЛЬКИМ одновременно (Пильный центр/ЧПУ/Присадка ЧПУ — до 3, у ФрауМебель все).
   * Для не-«Распил» категорий — пустой массив. Фильтр по одной выбранной подкатегории =
   * `includes`. Verbatim (P-001).
   */
  subcategories: z.array(z.string().min(1)),
  city: z.string().min(1),
  /** «Название компании» / имя. */
  name: z.string().min(1),
  address: z.string().nullable(),
  /** «Стационарный телефон». */
  phoneLandline: z.string().nullable(),
  /** «Мобильный телефон». */
  phoneMobile: z.string().nullable(),
  whatsapp: z.string().nullable(),
  telegram: z.string().nullable(),
  /** «Макс». */
  maxId: z.string().nullable(),
  /** Описание услуг — появится позже. */
  description: z.string().nullable(),
  /** Часы работы — появится позже. */
  workingHours: z.string().nullable(),
  /** Готовый signed URL (НЕ R2-ключ); null = нет фото. */
  photoUrl: z.string().nullable(),
});
export type Partner = z.infer<typeof PartnerSchema>;

/** Лёгкий item списка (Экран 2.8.2) — контакты для кнопок мессенджеров прямо в списке. */
export const PartnerListItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  city: z.string().min(1),
  address: z.string().nullable(),
  whatsapp: z.string().nullable(),
  telegram: z.string().nullable(),
  maxId: z.string().nullable(),
});
export type PartnerListItem = z.infer<typeof PartnerListItemSchema>;

export const PartnerListResponseSchema = PaginatedResponseSchema(PartnerListItemSchema);
export type PartnerListResponse = z.infer<typeof PartnerListResponseSchema>;

/**
 * GET /api/partners — фильтр по двум триггерам «Услуга» + «Город» (+ подкатегория
 * для «Распил…»).
 *
 * `category` и `city` ОБЯЗАТЕЛЬНЫ (в отличие от `name` фурнитуры/ADR-0067): «Услуга+
 * Город» — сам смысл запроса списка; их отсутствие = экран категорий 2.8.1 (фронт
 * туда и ведёт), а не list-вызов. `subcategory` опциональна.
 */
export const PartnerListQuerySchema = z
  .object({
    category: z.string().min(1),
    subcategory: z.string().optional(),
    city: z.string().min(1),
  })
  .merge(PaginationQuerySchema);

/** Output-тип после parse: `limit` всегда заполнен (default=50). */
export type PartnerListQuery = z.infer<typeof PartnerListQuerySchema>;
/** Input-тип до parse: `limit` опционален. */
export type PartnerListQueryInput = z.input<typeof PartnerListQuerySchema>;

// Города — переиспуем общий контракт `./handles` (ADR-0080 §2). НЕ дублируем и НЕ
// re-export'им: `CitiesResponseSchema`/`CitiesResponse` уже доступны из
// `@pomoshnik/shared` через `./handles`. Повторный re-export здесь сделал бы символ
// ambiguous в `export *` (index.ts) и молча исключил бы его, сломав потребителей (R1,
// как в furniture.ts). Клиент партнёров импортирует их из `./handles` напрямую.
